import { ButtonPrimary, ButtonLight } from 'components/Button'
import { RowBetween } from 'components/Row'
import { useActiveWeb3React } from 'hooks'
import React, { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import { PositionDetails } from 'types/position'
import ClaimPositionItem from './ClaimPositionItem'
import { CLAIM_ALL_ADDRESS } from '@hyperswap/addresses'
import { calculateGasMargin, getContract } from 'utils'
import CLAIM_ALL_ABI from '../../../abis/V3/ClaimAll.json'
import NonfungiblePositionManager_ABI from '../../../abis/V3/NonfungiblePositionManager.json'
import { NONFUNGIBLE_POSITION_MANAGER_ADDRESS } from '@hyperswap/addresses'
import { useTransactionAdder } from 'state/transactions/hooks'
import { Dots } from '../styleds'
import { BigNumber } from '@ethersproject/bignumber'
import { MouseoverTooltip } from 'components/Tooltip'
import { roundToDecimalPlace } from 'utils/data'
import TagButton from 'components/TagButton'
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  width: 100%;
  padding: 1rem;
  max-height: 75vh;
  overflow-y: auto;

  h2 {
    font-weight: 700;
    color: white;
    font-size: 20px;
    margin-bottom: 12px;
  }
`

const PoolContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  width: 100%;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
`

const WarningContainer = styled.div<{ estimatedGas: number; error: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  color: ${({ theme }) => theme.text2};
  font-size: 12px;
  background-color: ${({ theme }) => theme.bg1};

  ${({ error, estimatedGas }) =>
    (error || estimatedGas >= LIMIT_GAS.ERROR) &&
    css`
      background-color: rgba(253, 64, 64, 0.4);
      svg {
        width: 50px;
      }
    `}

  ${({ error, estimatedGas }) =>
    error === false &&
    estimatedGas >= LIMIT_GAS.WARNING &&
    estimatedGas < LIMIT_GAS.ERROR &&
    css`
      background-color: rgba(227, 165, 7, 0.1);
    `}
`

const LIMIT_GAS = {
  WARNING: 1500000,
  ERROR: 1900000,
  INFINITY: 99999999,
}

export default function ClaimAll({
  positions,
  onDismiss,
}: {
  positions: PositionDetails[] | undefined
  onDismiss: () => void
}) {
  const { account, library } = useActiveWeb3React()
  const addTransaction = useTransactionAdder()

  const [approval, setApproval] = useState<boolean>(false)
  const [loadingFetchApproval, setLoadingFetchApproval] = useState<boolean>(true)
  const [loadingApprove, setLoadingApprove] = useState<boolean>(false)
  const [loadingClaim, setLoadingClaim] = useState<boolean>(false)
  const [selectedPositions, setSelectedPositions] = useState<string[]>([])
  const [estimatedGas, setEstimatedGas] = useState<number>(0)
  const [estimatedGasError, setEstimatedGasError] = useState<boolean>(false)
  const [loadingEstimateGas, setLoadingEstimateGas] = useState<boolean>(false)

  React.useEffect(() => {
    const checkApproval = async () => {
      if (account && library) {
        const nonfungiblePositionManager = getContract(
          NONFUNGIBLE_POSITION_MANAGER_ADDRESS,
          NonfungiblePositionManager_ABI.abi,
          library,
          account
        )
        const allowance = await nonfungiblePositionManager.isApprovedForAll(account, CLAIM_ALL_ADDRESS)
        setApproval(allowance)
        setLoadingFetchApproval(false)
      }
    }
    if (account && library && loadingFetchApproval) {
      checkApproval()
    }
  }, [account, library, loadingFetchApproval])

  React.useEffect(() => {
    const estimateGas = async () => {
      if (account && library && selectedPositions.length > 0 && approval === true) {
        setLoadingEstimateGas(true)
        try {
          const claimAllContract = getContract(CLAIM_ALL_ADDRESS, CLAIM_ALL_ABI.abi, library, account)
          const estimate = claimAllContract.estimateGas.claimFeesForPositions

          // Convert token IDs to BigNumbers and validate
          const tokenIds = selectedPositions.map((position) => {
            const tokenId = BigNumber.from(position)
            if (!tokenId.gt(0)) {
              throw new Error(`Invalid token ID: ${position}`)
            }
            return tokenId
          })

          const estimatedGas = await estimate(tokenIds, {})
          setLoadingEstimateGas(false)

          setEstimatedGasError(false)
          setEstimatedGas(estimatedGas.toNumber())
        } catch (error) {
          console.error('Failed to estimate gas:', error)
          setLoadingEstimateGas(false)
          setEstimatedGasError(true)
          setEstimatedGas(LIMIT_GAS.INFINITY)
        }
      }
    }
    estimateGas()
  }, [account, library, selectedPositions, approval])

  const handleApprove = async () => {
    if (account && library) {
      const nonfungiblePositionManager = getContract(
        NONFUNGIBLE_POSITION_MANAGER_ADDRESS,
        NonfungiblePositionManager_ABI.abi,
        library,
        account
      )
      const estimate = nonfungiblePositionManager.estimateGas.setApprovalForAll
      const method = nonfungiblePositionManager.setApprovalForAll
      const args = [CLAIM_ALL_ADDRESS, true]
      setLoadingApprove(true)
      await estimate(...args, {})
        .then((estimatedGasLimit: any) => {
          return method(...args, {
            gasLimit: calculateGasMargin(estimatedGasLimit),
          }).then(async (response: any) => {
            addTransaction(response, {
              summary: `Approve`,
            })

            setTimeout(() => {
              setLoadingFetchApproval(true)
              setLoadingApprove(false)
            }, 4000)
          })
        })
        .catch((error: any) => {
          setLoadingApprove(false)
          console.error(error)
        })
    }
  }

  const handleClaim = async () => {
    if (account && library) {
      const claimAllContract = getContract(CLAIM_ALL_ADDRESS, CLAIM_ALL_ABI.abi, library, account)
      const estimate = claimAllContract.estimateGas.claimFeesForPositions
      const method = claimAllContract.claimFeesForPositions
      const args = [selectedPositions.map((position) => BigNumber.from(position))]
      setLoadingClaim(true)
      await estimate(...args, {})
        .then((estimatedGasLimit: any) => {
          return method(...args, {
            gasLimit: calculateGasMargin(estimatedGasLimit),
          }).then(async (response: any) => {
            addTransaction(response, {
              summary: `Claim All`,
            })
            setLoadingClaim(false)
            setSelectedPositions([])
            onDismiss()
          })
        })
        .catch((error: any) => {
          setLoadingClaim(false)
          console.error(error)
        })
    }
  }

  const handleSelectPosition = useCallback(
    (tokenId: string) => {
      if (selectedPositions.includes(tokenId)) {
        setSelectedPositions((prev) => prev.filter((id) => id !== tokenId))
      } else {
        setSelectedPositions((prev) => [...prev, tokenId])
      }
    },
    [selectedPositions]
  )

  const selectAllPositions = useCallback(() => {
    setSelectedPositions(
      positions
        ?.map((position) => (position?.liquidity?.gt(0) ? position.tokenId.toString() : ''))
        ?.filter((a) => a !== '') || []
    )
  }, [positions])

  const clearSelectedPositions = useCallback(() => {
    setSelectedPositions([])
  }, [])

  const isError = estimatedGasError || estimatedGas >= LIMIT_GAS.ERROR
  const isWarning = !isError && estimatedGas >= LIMIT_GAS.WARNING && estimatedGas < LIMIT_GAS.ERROR
  const isSuccess = !isError && !isWarning

  const allPositionsSelected =
    selectedPositions.length === positions?.filter((position) => position?.liquidity?.gt(0))?.length

  const noPositionsSelected = selectedPositions.length === 0

  return (
    <Container>
      <h2>Claim rewards</h2>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        {!allPositionsSelected && <TagButton onClick={() => selectAllPositions()}>Select all positions</TagButton>}
        {!noPositionsSelected && (
          <TagButton onClick={() => clearSelectedPositions()}>Clear selected positions</TagButton>
        )}
      </div>

      {positions && positions.length > 0 ? (
        <PoolContainer>
          {positions.map((position) => {
            return (
              <ClaimPositionItem
                key={position.tokenId.toString()}
                position={position}
                isSelected={selectedPositions.includes(position.tokenId.toString())}
                onSelect={() => handleSelectPosition(position.tokenId.toString())}
              />
            )
          })}
        </PoolContainer>
      ) : (
        <p style={{ textAlign: 'center', marginTop: '0', marginBottom: '12px' }}>No positions found.</p>
      )}

      {selectedPositions.length > 0 && approval === true && (
        <WarningContainer estimatedGas={estimatedGas} error={estimatedGasError}>
          {isError && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-exclamation-circle"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
              <path d="M12 9v4" />
              <path d="M12 16v.01" />
            </svg>
          )}
          {isWarning && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M12 9v4" />
              <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
              <path d="M12 16h.01" />
            </svg>
          )}

          <span>
            {(isSuccess || isWarning) && (
              <>
                Estimated Gas Amount:{' '}
                {loadingEstimateGas ? <Dots>Estimating</Dots> : roundToDecimalPlace(estimatedGas.toString(), '0')}
              </>
            )}
            {isError && (
              <>
                Estimated Gas Amount: {roundToDecimalPlace(estimatedGas.toString(), '0')}. This transaction may fail if
                you haven’t enabled big block support. You should consider unselecting some positions
              </>
            )}
            <div style={{ verticalAlign: 'middle', display: 'inline-block' }}>
              <MouseoverTooltip text={`In small block mode, transactions are limited to a maximum of 2 million gas.`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  style={{ marginLeft: '4px', width: 'unset' }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-info-circle"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                  <path d="M12 9h.01" />
                  <path d="M11 12h1v4h1" />
                </svg>
              </MouseoverTooltip>
            </div>
          </span>
        </WarningContainer>
      )}

      <RowBetween style={{ gap: '12px' }}>
        {approval ? (
          <ButtonPrimary
            disabled={selectedPositions.length === 0 || loadingClaim}
            onClick={() => handleClaim()}
            style={{ backgroundColor: isError ? '#FD4040' : '', color: isError ? 'white' : '' }}
          >
            {loadingClaim ? <Dots>Claiming</Dots> : isError ? 'Claim anyway' : 'Claim all'}
          </ButtonPrimary>
        ) : (
          <ButtonPrimary disabled={loadingApprove} onClick={() => handleApprove()}>
            {loadingApprove ? <Dots>Approving</Dots> : 'Approve'}
          </ButtonPrimary>
        )}
        <ButtonLight onClick={onDismiss}>Cancel</ButtonLight>
      </RowBetween>
    </Container>
  )
}
